import { Check, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/contexts/I18nContext';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { useUserProfile } from '@/features/profile/hooks/useUserProfile';

const RED    = '#d72e16';
const BG     = '#160c0c';
const SURFACE = '#231a1a';
const MUTED  = '#8d8d8d';

export default function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { profile } = useUserProfile();
  const isInstructor = profile?.is_instructor === true;
  const ownProfileRoute = profile?.username ? `/profile/${profile.username}` : '/profile';

  const headline = isInstructor
    ? t('premium.success.headline_instructor')
    : t('premium.success.headline_practitioner');

  const steps = isInstructor
    ? [
        { title: t('premium.success.step_1_title'), body: t('premium.success.step_1_body'), route: ownProfileRoute },
        { title: t('premium.success.step_2_title'), body: t('premium.success.step_2_body'), route: '/browse-instructors' },
        { title: t('premium.success.step_3_title'), body: t('premium.success.step_3_body'), route: '/settings/instructor-tools' },
      ]
    : [
        { title: t('premium.success.step_prac_1_title'), body: t('premium.success.step_prac_1_body'), route: '/browse-instructors' },
        { title: t('premium.success.step_prac_2_title'), body: t('premium.success.step_prac_2_body'), route: '/calendar' },
        { title: t('premium.success.step_prac_3_title'), body: t('premium.success.step_prac_3_body'), route: '/profile' },
      ];

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: BG }}>

      <TopNavigation variant="subtle" />

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto pt-16 pb-32">

        {/* Hero */}
        <div className="flex flex-col items-center px-4 pt-10">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: RED }}
          >
            <Check className="w-[38px] h-[38px] text-white" strokeWidth={2.5} />
          </div>
          <h1
            className="text-[24px] font-extrabold text-white text-center mt-8 leading-9"
            style={{ letterSpacing: '-0.4px' }}
          >
            {headline}
          </h1>
          <p
            className="text-[13px] text-center mt-3 max-w-[300px] leading-[19.5px]"
            style={{ color: MUTED }}
          >
            {t('premium.success.body')}
            <br />
            {t('premium.success.body_cta')}
          </p>
        </div>

        {/* Steps */}
        <div className="px-4 mt-8">
          <p
            className="text-[11px] font-bold uppercase mb-3 px-1"
            style={{ color: MUTED, letterSpacing: '1.5px' }}
          >
            {t('premium.success.next_steps_label')}
          </p>
          <div className="flex flex-col gap-2">
            {steps.map((step, i) => (
              <button
                key={i}
                data-testid={`success-step-${i + 1}`}
                onClick={() => navigate(step.route)}
                className="flex items-center gap-3.5 p-[15px] rounded-[14px] text-left w-full"
                style={{
                  backgroundColor: SURFACE,
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <div
                  className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center"
                  style={{
                    backgroundColor: 'rgba(215,46,22,0.1)',
                    border: '1px solid rgba(215,46,22,0.25)',
                  }}
                >
                  <span className="text-[13px] font-bold" style={{ color: RED }}>
                    {i + 1}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-semibold text-white leading-snug">
                    {step.title}
                  </p>
                  <p
                    className="text-[12px] leading-[16.8px] mt-0.5"
                    style={{ color: MUTED }}
                  >
                    {step.body}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: MUTED }} />
              </button>
            ))}
          </div>
        </div>

      </div>

      {/* Fixed footer with fade */}
      <div className="fixed bottom-0 inset-x-0 bg-gradient-to-t from-[#160c0c] to-transparent pt-10 pb-8 px-4 pointer-events-none">
        <button
          data-testid="success-primary-cta"
          onClick={() => navigate('/home')}
          className="w-full h-12 rounded-full text-[14px] font-medium text-white pointer-events-auto"
          style={{ border: '1.5px solid rgba(255,255,255,0.1)' }}
        >
          {t('premium.success.later')}
        </button>
      </div>

    </div>
  );
}
